import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ColumnSelector from "../DataProfiling/ColumnSelector";
import axios from "axios";
import toast from "react-hot-toast";
import heartLoading from "../../../../assests/heartLoading.gif";

// ─── AccordionItem ─────────────────────────────────────────────────────────────

const AccordionItem = memo(function AccordionItem({
  file,
  isExpanded,
  search,
  selectedColumns,
  columnDetails,
  onAccordionChange,
  onColumnSearchChange,
  onToggleColumn,
  onSelectAll,
  onCdeChange,
  onDescriptionChange,
  onMetadataChange,
  onTransformationLogicChange,
}) {
  const selectedCount = selectedColumns.length;

  const filteredColumns = useMemo(
    () =>
      file.columns.filter(
        (col) =>
          col.name.toLowerCase().includes(search.toLowerCase()) ||
          col.type.toLowerCase().includes(search.toLowerCase())
      ),
    [file.columns, search]
  );

  const handleSearchChange = useCallback(
    (e) => onColumnSearchChange(file.path, e.target.value),
    [file.path, onColumnSearchChange]
  );

  const handleSelectAll = useCallback(
    () => onSelectAll(filteredColumns.map((c) => c.id)),
    [filteredColumns, onSelectAll]
  );

  return (
    <div className="mb-3">
      <div className="flex justify-end mb-2">
        <span
          className={`text-xs px-3 py-1 rounded-full font-medium shadow-md ${
            selectedCount > 0
              ? "bg-green-500 text-white"
              : "bg-gray-300 text-gray-600"
          }`}
        >
          {selectedCount} column{selectedCount !== 1 ? "s" : ""} selected
        </span>
      </div>

      <Accordion
        expanded={isExpanded}
        onChange={onAccordionChange}
        disableGutters
        sx={{
          mb: 0,
          "&:before": { display: "none" },
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: "12px !important",
          overflow: "hidden",
          border: "1px solid #e5e7eb",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#bb1ccc", fontSize: 24 }} />}
          sx={{
            backgroundColor: "#f8fafc",
            "&:hover": { backgroundColor: "#f1f5f9" },
            transition: "background-color 0.2s ease",
          }}
        >
          <div className="flex flex-col">
            <Typography
              sx={{ fontWeight: 700, color: "black", fontSize: 16, mb: 0.5 }}
            >
              {file.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#6b7280", fontSize: 13 }}
            >
              {file.tableDescription}
            </Typography>
          </div>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            backgroundColor: "white",
            borderTop: "1px solid #e5e7eb",
            padding: "20px 24px",
          }}
        >
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search columns..."
              value={search}
              onChange={handleSearchChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: 14,
                border: "1px solid #d1d5db",
                borderRadius: 8,
                outline: "none",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#bb1ccc";
                e.target.style.borderWidth = "2px";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.borderWidth = "1px";
              }}
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <ColumnSelector
              columns={filteredColumns}
              selectedColumns={selectedColumns}
              onToggleColumn={onToggleColumn}
              onSelectAll={handleSelectAll}
              columnDetails={columnDetails}
              onCdeChange={onCdeChange}
              onDescriptionChange={onDescriptionChange}
              onMetadataChange={onMetadataChange}
              onTransformationLogicChange={onTransformationLogicChange}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
});

// ─── ColumnSelection ──────────────────────────────────────────────────────────

const ColumnSelection = ({ nextStep, id }) => {
  const [fetchedFiles, setFetchedFiles] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [columnSearch, setColumnSearch] = useState({});
  const [savingColumns, setSavingColumns] = useState(false);
  const [profilingColumns, setProfilingColumns] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const [columnSelection, setColumnSelection] = useState({});
  const [columnDetails, setColumnDetails] = useState({});

  const getFileKey = useCallback((file) => file.path, []);

  // ── allSelected — enables Continue button ────────────────────────────────

  const allSelected = useMemo(
    () =>
      fetchedFiles.length > 0 &&
      fetchedFiles.every((file) => {
        const fileKey = getFileKey(file);
        const sel = columnSelection[fileKey] || [];
        return (
          sel.length > 0 &&
          sel.every((colId) => {
            const d = columnDetails[fileKey]?.[colId] || {};
            return d.description && d.description.trim() !== "";
          })
        );
      }),
    [fetchedFiles, columnSelection, columnDetails, getFileKey]
  );

  // ── Accordion toggle ─────────────────────────────────────────────────────

  const handleAccordionChange = useCallback(
    (panel) => (_event, isExpanded) => {
      setExpandedAccordion(isExpanded ? panel : false);
    },
    []
  );

  // ── Column handlers ───────────────────────────────────────────────────────

  const handleToggleColumn = useCallback((fileKey, colId) => {
    setColumnSelection((prev) => {
      const already = prev[fileKey] || [];
      return {
        ...prev,
        [fileKey]: already.includes(colId)
          ? already.filter((id) => id !== colId)
          : [...already, colId],
      };
    });
  }, []);

  const handleSelectAllColumns = useCallback((fileKey, allColIds) => {
    setColumnSelection((prev) => {
      const already = prev[fileKey] || [];
      return {
        ...prev,
        [fileKey]: already.length === allColIds.length ? [] : allColIds,
      };
    });
  }, []);

  const handleColumnSearchChange = useCallback((fileKey, value) => {
    setColumnSearch((prev) => ({ ...prev, [fileKey]: value }));
  }, []);

  const handleCdeChange = useCallback((fileKey, colId, value) => {
    setColumnDetails((prev) => ({
      ...prev,
      [fileKey]: {
        ...prev[fileKey],
        [colId]: { ...prev[fileKey]?.[colId], cde: value },
      },
    }));
  }, []);

  const handleDescriptionChange = useCallback((fileKey, colId, value) => {
    setColumnDetails((prev) => ({
      ...prev,
      [fileKey]: {
        ...prev[fileKey],
        [colId]: { ...prev[fileKey]?.[colId], description: value },
      },
    }));
  }, []);

  const handleMetadataChange = useCallback((fileKey, colId, value) => {
    setColumnDetails((prev) => ({
      ...prev,
      [fileKey]: {
        ...prev[fileKey],
        [colId]: { ...prev[fileKey]?.[colId], metadata: value },
      },
    }));
  }, []);

  const handleTransformationLogicChange = useCallback(
    (fileKey, colId, value) => {
      setColumnDetails((prev) => ({
        ...prev,
        [fileKey]: {
          ...prev[fileKey],
          [colId]: {
            ...prev[fileKey]?.[colId],
            transformationLogic: value,
          },
        },
      }));
    },
    []
  );

  // ── perFileCallbacks — stable per file, rebuilt only when files change ────

  const perFileCallbacks = useMemo(() => {
    const map = {};
    fetchedFiles.forEach((file) => {
      const fileKey = getFileKey(file);
      map[fileKey] = {
        onAccordionChange: handleAccordionChange(fileKey),
        onToggleColumn: (colId) => handleToggleColumn(fileKey, colId),
        onSelectAll: (allColIds) => handleSelectAllColumns(fileKey, allColIds),
        onCdeChange: (colId, v) => handleCdeChange(fileKey, colId, v),
        onDescriptionChange: (colId, v) =>
          handleDescriptionChange(fileKey, colId, v),
        onMetadataChange: (colId, v) => handleMetadataChange(fileKey, colId, v),
        onTransformationLogicChange: (colId, v) =>
          handleTransformationLogicChange(fileKey, colId, v),
      };
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedFiles, getFileKey]);

  // ── Select All header button ─────────────────────────────────────────────

  const handleSelectAll = useCallback(() => {
    const ns = {},
      nd = {};
    fetchedFiles.forEach((file) => {
      const fileKey = getFileKey(file);
      ns[fileKey] = file.columns.map((c) => c.id);
      nd[fileKey] = {};
      file.columns.forEach((col) => {
        nd[fileKey][col.id] = {
          cde: false,
          description: "N/A",
          metadata: col.metadata || "",
          transformationLogic: "",
        };
      });
    });
    setColumnSelection(ns);
    setColumnDetails(nd);
  }, [fetchedFiles, getFileKey]);

  // ── Build API payload ────────────────────────────────────────────────────

  const buildSchemaList = useCallback(
    () =>
      fetchedFiles.map((file) => {
        const fileKey = getFileKey(file);
        return {
          name: file.name,
          path: file.path,
          source: file.source,
          columns: (columnSelection[fileKey] || []).map((colId) => {
            const col = file.columns.find((c) => c.id === colId);
            const d = columnDetails[fileKey]?.[colId] || {};
            return {
              id: col.id,
              name: col.name,
              type: col.type,
              cde: d.cde || false,
              description: d.description || "",
              metadata: d.metadata || col.metadata || "",
              transformationLogic: d.transformationLogic || "",
            };
          }),
        };
      }),
    [fetchedFiles, columnSelection, columnDetails, getFileKey]
  );

  // ── API calls ────────────────────────────────────────────────────────────

  const handleStartProfiling = useCallback(async () => {
    if (!allSelected) {
      toast.error(
        "Please select at least one column and fill in Description for each."
      );
      return;
    }
    setProfilingColumns(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_API_URL}/save-schema-metadata`,
        { product_id: id, schema_list: buildSchemaList() }
      );
      if (r?.data?.status === "success") {
        setProfilingColumns(false);
        nextStep();
      }
    } catch (e) {
      console.error(e);
      toast.error("Profiling API failed.");
      setProfilingColumns(false);
    }
  }, [allSelected, id, buildSchemaList, nextStep]);

  const handleSaveProfiling = useCallback(async () => {
    setSavingColumns(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_API_URL}/save-schema-only`,
        { product_id: id, schema_list: buildSchemaList() }
      );
      if (r?.data?.status === "success") {
        setSavingColumns(false);
        toast.success("Column Selection Saved Successfully");
      }
    } catch (e) {
      console.error(e);
      toast.error("Column Selection save API failed.");
      setSavingColumns(false);
    }
  }, [id, buildSchemaList]);

  // ── Fetch saved schema ───────────────────────────────────────────────────

  useEffect(() => {
    if (!id) return;
    const fetchSavedSchema = async () => {
      try {
        setLocalLoading(true);
        const r = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-schema-metadata/${id}`
        );
        if (r.status === 200 && Array.isArray(r.data?.schema_list)) {
          const saved = r.data.schema_list;
          setFetchedFiles(saved);
          setExpandedAccordion(saved[0] ? getFileKey(saved[0]) : false);

          const ns = {},
            nd = {};
          saved.forEach((file) => {
            const fileKey = getFileKey(file);
            ns[fileKey] = file.columns
              .filter((c) => c.isSelected === true)
              .map((c) => c.id);
            nd[fileKey] = {};
            file.columns.forEach((col) => {
              nd[fileKey][col.id] = {
                cde: col.cde || false,
                description: col.description || "",
                metadata: col.metadata || "",
                transformationLogic: col.transformationLogic ?? "",
              };
            });
          });
          setColumnSelection(ns);
          setColumnDetails(nd);
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load saved schema.");
      } finally {
        setLocalLoading(false);
      }
    };
    fetchSavedSchema();
  }, [id, getFileKey]);

  // ── Loading ──────────────────────────────────────────────────────────────

  if (localLoading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." className="h-30" />
          <span className="text-gray-700 text-[20px]">
            Hang tight! The resources are loading...
          </span>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="max-w-[90%] mx-auto px-4 py-6">
      <Box className="bg-white rounded-lg shadow-lg border border-gray-100">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-[#bb1ccc] mb-2">
                Select Columns to Profile
              </h2>
              <p className="text-gray-600 text-sm">
                Select columns to analyze data patterns and quality metrics.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                className="px-6 py-2.5 rounded-lg bg-[#bb1ccc] text-white font-medium hover:bg-[#000080] transition-colors duration-200 shadow-md hover:shadow-lg"
                onClick={handleSelectAll}
              >
                Select All
              </button>
              <button
                className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                  savingColumns
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : "bg-[#bb1ccc] text-white hover:bg-[#000080] shadow-md hover:shadow-lg"
                }`}
                onClick={handleSaveProfiling}
                disabled={savingColumns}
              >
                {savingColumns ? "Saving..." : "Save"}
              </button>
              <button
                className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                  !allSelected || profilingColumns
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : "bg-[#bb1ccc] text-white hover:bg-[#000080] shadow-md hover:shadow-lg"
                }`}
                onClick={handleStartProfiling}
                disabled={!allSelected || profilingColumns}
              >
                {profilingColumns ? "Continuing..." : "Continue"}
              </button>
            </div>
          </div>
        </div>

        {/* Accordion list */}
        <div className="p-6">
          {fetchedFiles.map((file) => {
            const fileKey = getFileKey(file);
            const cb = perFileCallbacks[fileKey] || {};

            return (
              <AccordionItem
                key={fileKey}
                file={file}
                isExpanded={expandedAccordion === fileKey}
                search={columnSearch[fileKey] || ""}
                selectedColumns={columnSelection[fileKey] || []}
                columnDetails={columnDetails[fileKey] || {}}
                onAccordionChange={cb.onAccordionChange}
                onColumnSearchChange={handleColumnSearchChange}
                onToggleColumn={cb.onToggleColumn}
                onSelectAll={cb.onSelectAll}
                onCdeChange={cb.onCdeChange}
                onDescriptionChange={cb.onDescriptionChange}
                onMetadataChange={cb.onMetadataChange}
                onTransformationLogicChange={cb.onTransformationLogicChange}
              />
            );
          })}
        </div>
      </Box>
    </div>
  );
};

export default ColumnSelection;